import { useEffect, useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { IoIosEyeOff, IoIosEye } from "react-icons/io";

import Button from "../../components/Button";
import TextInput from "../../components/TextInput";
import { toast } from "react-toastify";
import { AppRoutes } from "../../utils";
import { routePaths } from "../../constants";

interface FormProps {
  email: string;
  password: string;
}

interface ResetPasswordSearchParams {
  code: string;
}

export const Route = createFileRoute("/_auth/resetPassword")({
  component: ResetPassword,
  validateSearch: (
    search: Record<string, unknown>,
  ): ResetPasswordSearchParams => {
    return {
      code: search["code"] as string,
    };
  },
});

function ResetPassword() {
  const navigate = useNavigate();
  const { code } = useSearch({ from: Route.id }) as ResetPasswordSearchParams;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormProps>({ mode: "onSubmit", reValidateMode: "onChange" });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const onSubmit: SubmitHandler<FormProps> = async () => {
    setIsLoading(true);
  };

  async function verifyResetCode() {}

  useEffect(() => {
    if (!code) {
      toast.error("Reset code is missing");
      navigate({ to: AppRoutes.LOGIN });
      return;
    }

    verifyResetCode();
  }, [code, navigate]);

  const isValidForm = isLoading || Object.keys(errors).length === 0;

  return (
    <div className="reset-password-page flex flex-col">
      <h4 className="text-2xl font-bold text-center mt-52 mb-2">
        Reset Password
      </h4>

      <p className="text-center mb-10">
        To reset your password, enter your new password below.
      </p>

      <div className="form-container w-50 flex justify-center">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-field mb-8 w-96">
            <label
              className="block text-gray-700 text-sm font-medium mb-1"
              htmlFor="email"
            >
              Email
            </label>

            <TextInput
              className="form-control px-6 py-3 rounded-lg bg-gray-100"
              {...register("email", { required: "Email Address is required " })}
              placeholder="Enter your email"
              readOnly
              error={errors.email}
            />
          </div>

          <div className="form-field mb-8">
            <label
              className="block text-gray-700 text-sm font-medium mb-1"
              htmlFor="password"
            >
              Password
            </label>

            <div className="relative">
              <TextInput
                type={showPassword ? "text" : "password"}
                className="form-control w-full px-6 py-3 rounded-lg"
                {...register("password", { required: "Password is required" })}
                placeholder="Enter new password"
                error={errors.password}
              />
              <div
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              >
                {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
              </div>
            </div>
          </div>

          <Button
            className="bg-primary hover:bg-primary/80 transition-colors  text-white w-full py-3 ml-1 hover:border-accent rounded-lg border-primary"
            type="submit"
            disabled={!isValidForm}
            loading={isLoading}
          >
            Reset Password
          </Button>

          <div className="text-black text-center mt-5 mb-10">
            <Link to={routePaths.login} className="text-primary">
              ← Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
